import { createReducer } from '@reduxjs/toolkit'
import { TokenList } from '@uniswap/token-lists/dist/types'
import { DEFAULT_LIST_OF_LISTS, DEFAULT_TOKEN_LIST_URL } from '../../constants/lists'
import { updateVersion } from '../global/actions'
import { acceptListUpdate, addList, fetchTokenList, removeList, selectList, fetchPairList, PairList } from './actions'

export interface ListsState {
  readonly byUrl: {
    readonly [url: string]: {
      readonly current: TokenList | null
      readonly pendingUpdate: TokenList | null
      readonly loadingRequestId: string | null
      readonly error: string | null
    }
  }
  // this contains the default list of lists from the last time the updateVersion was called, i.e. the app was reloaded
  readonly lastInitializedDefaultListOfLists?: string[]
  readonly selectedListUrl: string | undefined
  // Pair list state
  readonly pairList: {
    readonly current: PairList | null
    readonly loadingRequestId: string | null
    readonly error: string | null
  }
}

const NEW_LIST_STATE: ListsState['byUrl'][string] = {
  error: null,
  current: null,
  loadingRequestId: null,
  pendingUpdate: null
}

type Mutable<T> = { -readonly [P in keyof T]: T[P] extends ReadonlyArray<infer U> ? U[] : T[P] }

const initialState: ListsState = {
  lastInitializedDefaultListOfLists: DEFAULT_LIST_OF_LISTS,
  byUrl: {
    ...DEFAULT_LIST_OF_LISTS.reduce<Mutable<ListsState['byUrl']>>((memo, listUrl) => {
      memo[listUrl] = NEW_LIST_STATE
      return memo
    }, {})
  },
  // Auto-select the default token list
  selectedListUrl: DEFAULT_TOKEN_LIST_URL || undefined,
  pairList: {
    current: null,
    loadingRequestId: null,
    error: null
  }
}

export default createReducer(initialState, builder =>
  builder
    .addCase(fetchTokenList.pending, (state, { payload: { requestId, url } }) => {
      const existing = state.byUrl[url]
      state.byUrl[url] = {
        current: existing?.current ?? null,
        pendingUpdate: existing?.pendingUpdate ?? null,
        loadingRequestId: requestId,
        error: null
      }
    })
    .addCase(fetchTokenList.fulfilled, (state, { payload: { requestId, tokenList, url } }) => {
      const loadingRequestId = state.byUrl[url]?.loadingRequestId

      // Always update if request matches - no version comparison
      if (loadingRequestId === null || loadingRequestId === requestId) {
        state.byUrl[url] = {
          current: tokenList,
          pendingUpdate: null,
          loadingRequestId: null,
          error: null
        }
      }
    })
    .addCase(fetchTokenList.rejected, (state, { payload: { url, requestId, errorMessage } }) => {
      if (state.byUrl[url]?.loadingRequestId !== requestId) {
        // no-op since it's not the latest request
        return
      }

      state.byUrl[url] = {
        current: null,
        pendingUpdate: null,
        loadingRequestId: null,
        error: errorMessage
      }
    })
    .addCase(selectList, (state, { payload: url }) => {
      state.selectedListUrl = url
      // automatically adds list
      if (!state.byUrl[url]) {
        state.byUrl[url] = NEW_LIST_STATE
      }
    })
    .addCase(addList, (state, { payload: url }) => {
      if (!state.byUrl[url]) {
        state.byUrl[url] = NEW_LIST_STATE
      }
    })
    .addCase(removeList, (state, { payload: url }) => {
      if (state.byUrl[url]) {
        delete state.byUrl[url]
      }
      if (state.selectedListUrl === url) {
        state.selectedListUrl = Object.keys(state.byUrl)[0]
      }
    })
    .addCase(acceptListUpdate, (state, { payload: url }) => {
      if (!state.byUrl[url]?.pendingUpdate) {
        throw new Error('accept list update called without pending update')
      }
      state.byUrl[url] = {
        current: state.byUrl[url].pendingUpdate,
        pendingUpdate: null,
        loadingRequestId: null,
        error: null
      }
    })
    .addCase(updateVersion, state => {
      // state loaded from localStorage, but new lists have never been initialized
      if (!state.lastInitializedDefaultListOfLists) {
        state.byUrl = initialState.byUrl
        state.selectedListUrl = undefined
      } else if (state.lastInitializedDefaultListOfLists) {
        const lastInitializedSet = state.lastInitializedDefaultListOfLists.reduce<Set<string>>(
          (s, l) => s.add(l),
          new Set()
        )
        const newListOfListsSet = DEFAULT_LIST_OF_LISTS.reduce<Set<string>>((s, l) => s.add(l), new Set())

        DEFAULT_LIST_OF_LISTS.forEach(listUrl => {
          if (!lastInitializedSet.has(listUrl)) {
            state.byUrl[listUrl] = NEW_LIST_STATE
          }
        })

        state.lastInitializedDefaultListOfLists.forEach(listUrl => {
          if (!newListOfListsSet.has(listUrl)) {
            delete state.byUrl[listUrl]
          }
        })
      }

      state.lastInitializedDefaultListOfLists = DEFAULT_LIST_OF_LISTS

      // Auto-select default token list if no list is selected or current selection is invalid
      if (DEFAULT_TOKEN_LIST_URL) {
        if (!state.selectedListUrl || !state.byUrl[state.selectedListUrl]) {
          state.selectedListUrl = DEFAULT_TOKEN_LIST_URL
        }
      }

      // Initialize pairList if not present (migration from old state)
      if (!state.pairList) {
        state.pairList = {
          current: null,
          loadingRequestId: null,
          error: null
        }
      }
    })
    // Pair list reducers
    .addCase(fetchPairList.pending, (state, { payload: { requestId } }) => {
      state.pairList = {
        current: state.pairList?.current ?? null,
        loadingRequestId: requestId,
        error: null
      }
    })
    .addCase(fetchPairList.fulfilled, (state, { payload: { pairList, requestId } }) => {
      const loadingRequestId = state.pairList?.loadingRequestId
      if (loadingRequestId === null || loadingRequestId === undefined || loadingRequestId === requestId) {
        state.pairList = {
          current: pairList,
          loadingRequestId: null,
          error: null
        }
      }
    })
    .addCase(fetchPairList.rejected, (state, { payload: { errorMessage, requestId } }) => {
      if (state.pairList?.loadingRequestId !== requestId) {
        return
      }
      state.pairList = {
        current: state.pairList?.current ?? null,
        loadingRequestId: null,
        error: errorMessage
      }
    })
)
